/**
 * GitLabCISetupWizard Component
 *
 * Setup wizard for adding LeftShield to GitLab CI pipelines.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '650px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

interface GitLabCISetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

export const GitLabCISetupWizard: React.FC<GitLabCISetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<number | null>(null);
  const [failOn, setFailOn] = useState<'block' | 'warn'>('block');

  const copyToClipboard = (text: string, step: number) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const gitlabCIYaml = `include:
  - remote: 'https://raw.githubusercontent.com/leftshield/gitlab-ci/main/templates/leftshield-validate.yml'

stages:
  - validate
  - build
  - test
  - deploy

leftshield-scan:
  extends: .leftshield-validate
  stage: validate
  variables:
    LEFTSHIELD_API_KEY: $LEFTSHIELD_API_KEY
    LEFTSHIELD_FAIL_ON: ${failOn}
    LEFTSHIELD_SCAN_TYPES: "sast,secrets,sca"
  rules:
    - if: $CI_PIPELINE_SOURCE == "merge_request_event"
    - if: $CI_COMMIT_BRANCH == $CI_DEFAULT_BRANCH`;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up GitLab CI</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                The LeftShield GitLab CI template integrates security scanning into your merge request and push pipelines.
              </MessageBarBody>
            </MessageBar>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <Text weight="semibold">Add API Key as CI/CD Variable</Text>
              </div>
              <Text size={200}>
                Go to Settings → CI/CD → Variables → Add variable:
              </Text>
              <div className={styles.codeBlock}>
                <Text size={200}>
                  <strong>Key:</strong> LEFTSHIELD_API_KEY{'\n'}
                  <strong>Value:</strong> {apiKey}{'\n'}
                  <strong>Type:</strong> Variable{'\n'}
                  <strong>Protect variable:</strong> Recommended{'\n'}
                  <strong>Mask variable:</strong> Yes
                </Text>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 1 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(apiKey, 1)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <Text weight="semibold">Configure Failure Behavior</Text>
              </div>
              <RadioGroup
                value={failOn}
                onChange={(_, data) => setFailOn(data.value as 'block' | 'warn')}
              >
                <div className={styles.option}>
                  <Radio value="block" label="Fail on blocking violations (recommended)" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    Pipelines will fail if critical/high severity issues are found
                  </Text>
                </div>
                <div className={styles.option}>
                  <Radio value="warn" label="Fail on any violations" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    Pipelines will fail for any enforcement decision
                  </Text>
                </div>
              </RadioGroup>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>3</span>
                <Text weight="semibold">Update .gitlab-ci.yml</Text>
              </div>
              <Text size={200}>
                Add the following to your <code>.gitlab-ci.yml</code>:
              </Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{gitlabCIYaml}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 3 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(gitlabCIYaml, 3)}
                />
              </div>
            </div>

            <Divider />

            <MessageBar intent="success">
              <MessageBarBody>
                Security scan results will be displayed in the merge request widget.{' '}
                <Link href="https://docs.leftshield.io/gitlab-ci" target="_blank">
                  View documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
